import React, { useEffect, useRef } from 'react';
import { useSettingsStore } from '../../store/settingsStore';

export type WeatherType = 'none' | 'snow' | 'blizzard' | 'rain' | 'embers' | 'ash';

interface WeatherProps {
  type: WeatherType;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  life: number;
  maxLife: number;
}

const WeatherEffect: React.FC<WeatherProps> = ({ type }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { reducedMotion } = useSettingsStore();

  useEffect(() => {
    if (type === 'none' || reducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    let width = window.innerWidth;
    let height = window.innerHeight;

    // Configuration based on weather type
    const config = {
      snow: { count: 150, speedY: 1.5, speedX: 0.5, size: 2, color: '255, 255, 255' },
      blizzard: { count: 400, speedY: 0.5, speedX: 15, size: 1.5, color: '200, 220, 255' }, // Horizontal movement
      rain: { count: 300, speedY: 15, speedX: 0, size: 1, color: '180, 200, 220' },
      embers: { count: 60, speedY: -1, speedX: 0.5, size: 2, color: '255, 100, 50' },
      ash: { count: 100, speedY: 0.5, speedX: 0.2, size: 1.5, color: '150, 150, 150' },
    };

    const currentConfig = config[type as keyof typeof config] || config.snow;

    // Resize handler
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();

    // Initialize particles
    const createParticle = (reset = false): Particle => {
      const isBlizzard = type === 'blizzard';
      const isEmbers = type === 'embers';
      
      return {
        x: Math.random() * width,
        y: reset ? (isEmbers ? height + 10 : -10) : Math.random() * height,
        vx: (Math.random() - 0.5) * currentConfig.speedX + (isBlizzard ? -currentConfig.speedX : 0),
        vy: currentConfig.speedY * (0.5 + Math.random()), // Vary speed
        size: Math.random() * currentConfig.size + 0.5,
        alpha: Math.random() * 0.5 + 0.3,
        life: Math.random() * 100,
        maxLife: 100 + Math.random() * 100
      };
    };

    particles = Array.from({ length: currentConfig.count }, () => createParticle());

    // Animation Loop
    const render = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p, i) => {
        // Update position
        p.x += p.vx;
        p.y += p.vy;
        p.life--;

        // Draw
        ctx.beginPath();
        ctx.fillStyle = `rgba(${currentConfig.color}, ${p.alpha})`;
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Reset if out of bounds or dead
        const isBlizzard = type === 'blizzard';
        const isEmbers = type === 'embers';

        if (
          (isEmbers && p.y < -10) ||
          (!isEmbers && p.y > height) ||
          (p.x < -10 || p.x > width + 10) ||
          p.life <= 0
        ) {
          particles[i] = createParticle(true);
          // For blizzard, spawn on the right side mostly
          if (isBlizzard) {
            particles[i].x = width + Math.random() * 50;
            particles[i].y = Math.random() * height;
          }
        }
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [type, reducedMotion]);

  if (type === 'none' || reducedMotion) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-10 opacity-80"
      style={{ mixBlendMode: type === 'embers' ? 'screen' : 'normal' }}
    />
  );
};

export default WeatherEffect;
